import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
} from '@angular/core'
import {
  IonApp,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonRouterOutlet,
  IonSplitPane,
  Platform,
  IonRouterLink,
  MenuController,
} from '@ionic/angular'
import { filter, map } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { TranslateService, TranslatePipe } from '@ngx-translate/core'
import { Article } from './models/article'
import { Page } from './models/page'
import { MOBILE_APP_PAGE_SLUG } from './models/page'
import { StateService } from './services/state.service'
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterLink } from '@angular/router'
import { SeoContentService } from './services/seo-content.service'
import packageInfo from '../../package.json'
import { StorageService } from './services/storage.service'
import { LanguageService } from './services/language.service'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { RateService } from './services/rate.service'
import { inject as vercelInject } from '@vercel/analytics'
import { FirebaseAnalyticsService } from './services/firebase-analytics.service'
import { isNoMonitorAndProd } from './helpers/check-for-monitor-and-prod.helper'
import { Capacitor } from '@capacitor/core'
import { captureException } from './helpers/error-capture.helper'
import { YoutubeService } from './services/youtube.service'
import { SeoData } from './models/seo-data'
import { AnalyticsEvents } from './models/analytics-events'
import { createCustomElement } from '@angular/elements'
import { ImageComponent } from './components/image.component'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    IonApp,
    IonSplitPane,
    IonMenu,
    IonContent,
    IonList,
    IonMenuToggle,
    IonItem,
    IonLabel,
    IonRouterOutlet,
    IonRouterLink,
    TranslatePipe,
  ],
})
export class AppComponent implements OnInit {
  private platform = inject(Platform)
  private stateService = inject(StateService)
  translate = inject(TranslateService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private browserTitleService = inject(SeoContentService)
  private firebaseAnalyticsService = inject(FirebaseAnalyticsService)
  private storageService = inject(StorageService)
  private languageService = inject(LanguageService)
  private rateService = inject(RateService)
  private youtubeService = inject(YoutubeService)
  private menuController = inject(MenuController)
  private destroyRef = inject(DestroyRef)
  private injector = inject(Injector)

  readonly articles: Signal<Array<Article> | undefined>
  readonly pages: Signal<Array<Page> | undefined>
  readonly targetGroup: Signal<'pre-existing' | 'gestational' | undefined>
  readonly currentLanguage: Signal<string | undefined>
  readonly isWeb = !Capacitor.isNativePlatform()

  constructor() {
    const translate = this.translate

    translate.addLangs(['en', 'da', 'sv', 'au'])
    translate.setFallbackLang(this.languageService.getSystemLanguage())

    const element = createCustomElement(ImageComponent, { injector: this.injector })
    customElements.define('app-image', element)

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.menuController.close('root-menu').catch(() => undefined)

        // pause all YouTube videos when navigating away from the page
        document.querySelectorAll('iframe').forEach((item) => {
          item?.contentWindow?.postMessage('pause-video', '*')
        })
      })

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => {
          const child = this.activatedRoute.firstChild
          const path = (event as NavigationEnd).url
          return {
            path,
            seoData: this.browserTitleService.getTitle(child?.snapshot.data.title || '', path),
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(async ({ path, seoData }: { path: string; seoData: Promise<SeoData> }) => {
        try {
          const seoContent = await seoData
          if (seoContent.title) {
            await this.firebaseAnalyticsService.pageInit(path, seoContent.title)
          }
        } catch (error) {
          captureException(error, 'info', 'seo-data')
        }
      })
    this.articles = this.stateService.allArticlesSorted
    this.pages = computed(() => this.stateService.pages().filter((page) => page.slug !== MOBILE_APP_PAGE_SLUG))
    this.targetGroup = this.stateService.targetGroup
    this.currentLanguage = this.stateService.currentLanguage
  }

  ngOnInit() {
    void this.#initializeBootstrap()
  }

  async #initializeBootstrap(): Promise<void> {
    await this.#withBootstrapCatch(async () => {
      await this.platform.ready()

      const sessions = await this.storageService.get('sessions')
      await this.storageService.set('sessions', (Number(sessions) || 0) + 1)

      if (Capacitor.isNativePlatform()) {
        try {
          await StatusBar.setBackgroundColor({ color: '#4a275e' })
          await StatusBar.setOverlaysWebView({ overlay: false })
          await StatusBar.setStyle({ style: Style.Dark })
          await SplashScreen.hide()
          if (sessions > 1) {
            this.rateService.init()
          }
        } catch (error) {
          captureException(error, 'error', 'plugins')
        }
      }

      const medicalTrail = this.stateService.medicalTrail()
      if (medicalTrail) {
        await this.firebaseAnalyticsService.setUserProperty('medicalTrail', medicalTrail)
      }

      const platform = this.firebaseAnalyticsService.platformName()
      await this.firebaseAnalyticsService.trackEvent('start_session', { platform })

      const startFirst = await this.storageService.get('startFirst')
      if (!startFirst) {
        await this.firebaseAnalyticsService.trackEvent('start', { version: packageInfo.version, platform })
        await this.storageService.set('startFirst', packageInfo.version)
      } else if (startFirst !== packageInfo.version) {
        await this.firebaseAnalyticsService.trackEvent('update', { version: packageInfo.version, platform })
        await this.storageService.set('startFirst', packageInfo.version)
      }

      if (isNoMonitorAndProd() && vercelInject && !Capacitor.isNativePlatform()) {
        try {
          vercelInject()
        } catch (error) {
          captureException(error, 'info', 'vercel-analytics-inject')
        }
      }

      this.youtubeService.init()
    })
  }

  async #withBootstrapCatch(work: () => Promise<void>): Promise<void> {
    try {
      await work()
    } catch (error) {
      captureException(error, 'error', 'app-component-bootstrap')
    }
  }

  setTargetGroup(targetGroup: 'pre-existing' | 'gestational') {
    this.stateService.setTargetGroup(targetGroup)
    const eventName =
      targetGroup === 'gestational'
        ? AnalyticsEvents.TARGET_GROUP_GESTATIONAL
        : AnalyticsEvents.TARGET_GROUP_PRE_EXISTING
    this.firebaseAnalyticsService.trackEvent(eventName, {})
    this.router.navigate(['/'])
  }
}
